import { pdf } from "@react-pdf/renderer";
import * as Sentry from "@sentry/react";
import { Download } from "lucide-react";
import { useCallback, useMemo } from "react";

import { getStationName, getWagonCount, useRebiddingTableData } from "./model";
import {
  OwnerProposalForm,
  OwnerRebiddingForm,
  OwnerSummingUpForm,
  SobRebiddingForm,
  TradeDirectionRow,
  ModerationActions,
  SobOfferForm,
  TradeResultsPDF,
  GridTitle,
  Grid,
  GridSection,
  GridRow,
  BoldCell,
  NormalCell,
  TradeSummaryRow,
  TradeSummaryInlineRow,
} from "./ui";

import { OrderDetailResponseDTOStatus, useGetCargoOrder, useGetWinnersResult } from "@/common/api";
import { Card, ErrorMessage, LoadingModal, Button } from "@/common/components";
import { useAuth, useUser } from "@/common/hooks";
import { formatDatetime, formatDate } from "@/common/lib";

interface ViewOrderPageProps {
  orderId: string;
}

export function ViewOrderPage({ orderId }: ViewOrderPageProps) {
  const {
    auth: { isWagonOwner, isCargoOwner, isAdmin },
  } = useAuth();

  const { user } = useUser();

  const { data, isLoading, error, refetch } = useGetCargoOrder(
    { id: orderId, userId: user?.id! },
    {
      query: {
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );

  const details = data?.data.payload;
  const hasRetrading = Boolean(details?.retrading);

  // Признаки прав для управления видимостью кнопок действий.
  // Критические действия доступны только инициатору заявки; коллеги из той же
  // компании видят заявку и ход торгов, но кнопки действий им не показываются.
  const isСolleague = Boolean(details?.sameCompanyAsOwner); // коллега ГВ
  const isMyParticipation = Boolean(details?.isParticipant); // собственный отклик пользователя (СОБ)
  const companyHasParticipation = Boolean(details?.companyHasParticipation); // отклик уже есть у компании

  const [isCollectProposal, isRebidding, isSummingUp, isCompleted, isModeration] = [
    details?.status === OrderDetailResponseDTOStatus.COLLECT_PROPOSAL,
    details?.status === OrderDetailResponseDTOStatus.REBIDDING,
    details?.status === OrderDetailResponseDTOStatus.SUMMING_UP,
    details?.status === OrderDetailResponseDTOStatus.COMPLETED,
    details?.status === OrderDetailResponseDTOStatus.ON_MODERATION,
  ];

  const isStartStages = isCollectProposal || isRebidding || (!hasRetrading && isSummingUp);
  const isEndStages = isRebidding || (hasRetrading && isSummingUp) || isCompleted;

  const { data: winnersData } = useGetWinnersResult(
    { orderId, userId: user?.id! },
    {
      query: {
        enabled: !!user?.id && !!orderId && isCompleted,
      },
    },
  );

  const [isOrderOwner, isWagonWinner, isWagonParticipant] = [
    isCargoOwner && winnersData?.data?.payload?.isOrderOwner === true, //  ГВ хозяин заявки - видит все
    isWagonOwner &&
      winnersData?.data?.payload?.winners?.some((winner) => winner.userId === user?.id), // СОБ победитель
    isWagonOwner && winnersData?.data?.payload?.isTradeParticipant === true, // СОБ участник сделки
  ];

  const winner = useMemo(
    () => winnersData?.data?.payload?.winners?.find((winner) => winner.userId === user?.id!),
    [winnersData?.data?.payload?.winners, user?.id],
  );

  const getCompanyWinner = useCallback(
    (company: string) =>
      winnersData?.data?.payload?.winners?.find((winner) => winner.companyName === company),
    [winnersData?.data?.payload?.winners],
  );

  const winnersCompany = winnersData?.data?.payload?.winnersCompany;
  const directions = winnersData?.data?.payload?.directions;
  const participants = winnersData?.data?.payload?.participant;

  // Получаем данные для сводной таблицы по переторжке через хук
  const {
    tableRebiddingData,
    tableParticipants,
    proposals,
    maxRebiddingCount,
    totalWagonsRequired,
  } = useRebiddingTableData({
    orderId,
    enabled: isEndStages,
  });

  const isParticipant = useMemo(() => {
    const searchParams = new URLSearchParams(document.location.search);
    return searchParams.get("participant") === "1";
  }, []);

  // Функция для генерации и скачивания PDF
  const handleExportToExcel = useCallback(async () => {
    if (!details) return;

    try {
      const doc = (
        <TradeResultsPDF
          order={details}
          winnersCompany={winnersCompany}
          directions={directions}
          participants={participants}
          getCompanyWinner={getCompanyWinner}
          tableRebiddingData={tableRebiddingData}
          tableParticipants={tableParticipants}
          proposals={proposals}
          maxRebiddingCount={maxRebiddingCount}
          totalWagonsRequired={totalWagonsRequired}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Итоги  - ${details.orderNumber ?? details.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "pdf-generation" },
        extra: { orderId: details?.id, orderNumber: details?.orderNumber },
      });
    }
  }, [
    details,
    winnersCompany,
    directions,
    participants,
    getCompanyWinner,
    tableRebiddingData,
    tableParticipants,
    proposals,
    maxRebiddingCount,
    totalWagonsRequired,
  ]);

  if (isLoading) {
    return <LoadingModal isOpen={true} />;
  }

  if (error || !details) {
    return <ErrorMessage />;
  }

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center md:flex-row flex-col md:gap-4 gap-0">
        <div className="pt-0 md:pt-6 mb-6 flex flex-col gap-2">
          <h1 className="text-Main-orange text-2xl font-bold uppercase leading-8">
            Заявка {details.orderNumber ?? details.id}
          </h1>
          <p className="text-Text-secondary text-base font-normal leading-5">
            Детальная информация о заявке
          </p>
          {isAdmin && details.ownerCompanyName && (
            <p className="text-Text-main text-base leading-5 break-words">
              <span className="font-semibold">ГВ — инициатор заявки: </span>
              {details.ownerCompanyName}
            </p>
          )}
        </div>
        {isModeration && isAdmin && (
          <ModerationActions
            orderId={details.id!}
            isModeration={isModeration}
            onSuccess={() => refetch()}
          />
        )}
      </div>

      <div>
        <Card className="border border-Border-gray rounded-none overflow-hidden !p-0 bg-transparent">
          {/* 1 */}
          <Grid>
            {/* Left */}
            <GridSection>
              <GridTitle>Направление о перевозке</GridTitle>
              <GridRow>
                <BoldCell>Станция погрузки</BoldCell>
                <NormalCell>
                  {details.loadingStation &&
                    `${details.loadingStation?.name} (${details.loadingStation?.road})`}
                </NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Дорога погрузки</BoldCell>
                <NormalCell>{details.loadingRoad?.fullName}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Станции выгрузки ж/д дорог</BoldCell>
                <div>
                  {details.unloadingDirection?.map((item) => (
                    <NormalCell key={item.indexNumber}>
                      {getStationName(item.unloadingRoad, item.unloadingStation)}{" "}
                      {item.countWagons ? `- ${getWagonCount(item.countWagons)}` : null}
                    </NormalCell>
                  ))}
                </div>
              </GridRow>
            </GridSection>
            {/* Right */}
            <GridSection>
              <GridTitle>Информация о вагонах</GridTitle>
              <GridRow>
                <BoldCell>Тип вагона</BoldCell>
                <NormalCell>{details.wagonType}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Модель вагона</BoldCell>
                <NormalCell>{details.modelId?.model}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Грузоподъемность (т)</BoldCell>
                <NormalCell>
                  {details.modelId?.liftingCapacity || details.liftingCapacity || "нет данных"}
                </NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Объем (м3)</BoldCell>
                <NormalCell>
                  {details.modelId?.bodySize || details.bodySize || "нет данных"}
                </NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Общее количество вагонов:</BoldCell>
                <NormalCell>{details.totalWagons}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Доп. требования по вагонам</BoldCell>
                <NormalCell>{details.cargoComments}</NormalCell>
              </GridRow>
            </GridSection>
          </Grid>
          {/* 2 */}
          <Grid>
            {/* Left */}
            <GridSection>
              <GridTitle>Информация о грузе</GridTitle>
              <GridRow>
                <BoldCell>Наименование груза</BoldCell>
                <NormalCell>{details.cargoId?.cargoName}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Код ЕТСНГ</BoldCell>
                <NormalCell>{details.cargoId?.cargoCode}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Вес груза</BoldCell>
                <NormalCell>{details.cargoWeight} тонн</NormalCell>
              </GridRow>
            </GridSection>
            {/* Right */}
            <GridSection>
              <GridTitle>Нормативы</GridTitle>
              <GridRow>
                <BoldCell>Норматив на погрузку (сутки)</BoldCell>
                <NormalCell>
                  {details.standartTimeLoading === 0 ? "нет данных" : details.standartTimeLoading}
                </NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Норматив на выгрузку (сутки)</BoldCell>
                <NormalCell>
                  {details.standartTimeUnloading === 0
                    ? "нет данных"
                    : details.standartTimeUnloading}
                </NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Штраф</BoldCell>
                <NormalCell>{details.penalty} руб./сутки</NormalCell>
              </GridRow>
            </GridSection>
          </Grid>
          {/* 3 */}
          <Grid>
            {/* Left */}
            <GridSection>
              <GridTitle>Этапы сбора предложений</GridTitle>
              <GridRow>
                <BoldCell>Начало сессии (время начала первого этапа)</BoldCell>
                <NormalCell>{formatDatetime(details.tradeBegin)}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Окончание сессии (время окончания последнего этапа)</BoldCell>
                <NormalCell>{formatDatetime(details.tradeEnd)}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>
                  1 этап
                  <br />
                  Сбор предложений
                </BoldCell>
                <NormalCell>
                  {`${formatDatetime(details.collectionBegin) ?? ""} - ${formatDatetime(details.collectionEnd) ?? ""}`}
                </NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>
                  2 этап
                  <br />
                  Переторжка
                </BoldCell>
                <NormalCell>
                  {`${formatDatetime(details.retradingBegin) ?? ""} - ${formatDatetime(details.retradingEnd) ?? ""}`}
                </NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>
                  3 этап
                  <br />
                  Подведение итогов
                </BoldCell>
                <NormalCell>{formatDatetime(details.tradeEnd) ?? ""}</NormalCell>
              </GridRow>
            </GridSection>
            {/* Right */}
            <GridSection>
              <GridTitle>График подачи вагонов</GridTitle>
              <GridRow>
                <BoldCell>Срок подачи вагонов С</BoldCell>
                <NormalCell>{formatDate(details.deadlineAt)}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Срок подачи вагонов По</BoldCell>
                <NormalCell>{formatDate(details.deadlineTo)}</NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Количество вагонов по декадам</BoldCell>
                <NormalCell>
                  <span className="block">
                    1 - {details.decadeFirst ? getWagonCount(details.decadeFirst) : "нет данных"}
                  </span>
                  <span className="block">
                    2 - {details.decadeSecond ? getWagonCount(details.decadeSecond) : "нет данных"}
                  </span>
                  <span className="block">
                    3 - {details.decadeThird ? getWagonCount(details.decadeThird) : "нет данных"}
                  </span>
                </NormalCell>
              </GridRow>
              <GridRow>
                <BoldCell>Особые отметки</BoldCell>
                <NormalCell>{details.decadeComments}</NormalCell>
              </GridRow>
            </GridSection>

            {isCompleted && (
              <div className="col-span-full md:col-span-2">
                <GridSection>
                  <GridTitle>Итоги </GridTitle>
                  {/* СОБ - участник и победитель */}
                  {(isWagonWinner || isWagonParticipant) && (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 text-sm leading-5">
                        <BoldCell>Победители</BoldCell>
                        <div className="col-span-1 md:col-span-3 flex flex-col py-3 md:py-5">
                          {winnersCompany && winnersCompany.length > 0 ? (
                            winnersCompany.map((company, index) => (
                              <p key={index} className="font-medium text-sm leading-5 py-1">
                                {company}
                              </p>
                            ))
                          ) : (
                            <p className="font-normal text-sm leading-5 py-1 text-Text-secondary">
                              Победители не были выбраны
                            </p>
                          )}
                        </div>
                      </div>
                      {/* СОБ - победитель */}
                      {isWagonWinner && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-4 text-sm leading-5">
                            <BoldCell>Мое предложение</BoldCell>
                            <div className="col-span-3 px-4 md:px-0 py-3 md:py-5">
                              {directions?.map((item) => {
                                const offer = winner?.offers?.find(
                                  (offer) => offer.indexNumber === item.indexNumber,
                                );
                                return (
                                  <TradeDirectionRow
                                    key={item.indexNumber}
                                    station={
                                      getStationName(item.unloadingRoad, item.unloadingStation) ??
                                      "-"
                                    }
                                    tariff={item.tariffRoad ?? null}
                                    offer={offer?.wagonTariff ?? null}
                                  />
                                );
                              })}
                            </div>
                          </div>
                          <TradeSummaryRow
                            label="Предложено"
                            value={
                              winner?.countWagonsOwner ? `${winner?.countWagonsOwner} в.` : "-"
                            }
                          />
                          <TradeSummaryRow
                            label="Согласовано"
                            value={
                              winner?.numberAgreedWagons ? `${winner?.numberAgreedWagons} в.` : "-"
                            }
                          />
                        </>
                      )}
                    </>
                  )}
                  {/* ГВ - участник и АДМИН  */}
                  {(isAdmin || isOrderOwner) && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-4 text-sm leading-5">
                        <BoldCell>Победители</BoldCell>
                        <div className="col-span-1 md:col-span-3 flex flex-col py-3 md:py-5 px-5 md:px-10">
                          {winnersCompany && winnersCompany.length > 0 ? (
                            winnersCompany.map((company, index) => {
                              const companyWinner = getCompanyWinner(company);
                              return (
                                <div key={index} className="mb-10 last:mb-0">
                                  <p className="font-medium text-sm leading-5 py-1 mb-4">
                                    {company}
                                  </p>
                                  {directions?.map((item) => {
                                    const offer = companyWinner?.offers?.find(
                                      (offer) => offer.indexNumber === item.indexNumber,
                                    );
                                    return (
                                      <TradeDirectionRow
                                        key={item.indexNumber}
                                        station={
                                          getStationName(
                                            item.unloadingRoad,
                                            item.unloadingStation,
                                          ) ?? "-"
                                        }
                                        tariff={item.tariffRoad ?? null}
                                        offer={offer?.wagonTariff ?? null}
                                      />
                                    );
                                  })}
                                  <div className="pt-0 md:pt-5">
                                    <TradeSummaryInlineRow
                                      label="Предложено"
                                      value={
                                        companyWinner?.countWagonsOwner
                                          ? `${companyWinner?.countWagonsOwner} в.`
                                          : "-"
                                      }
                                    />
                                    <TradeSummaryInlineRow
                                      label="Согласовано"
                                      value={
                                        companyWinner?.numberAgreedWagons
                                          ? `${companyWinner?.numberAgreedWagons} в.`
                                          : "-"
                                      }
                                    />
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <p className="font-normal text-sm leading-5 py-1 text-Text-secondary">
                              Победители не были выбраны
                            </p>
                          )}
                        </div>
                      </div>
                      {participants && participants.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-4 text-sm leading-5">
                          <BoldCell>Участники</BoldCell>
                          <div className="col-span-1 md:col-span-3 flex flex-col py-3 md:py-5 px-5 md:px-10">
                            {participants.map((participant, index) => (
                              <div key={index} className="mb-5 last:mb-0">
                                <p className="font-medium text-sm leading-5 py-1">
                                  {participant.companyName}
                                </p>
                                {directions?.map((item) => {
                                  const offer = participant?.offers?.find(
                                    (offer) => offer.indexNumber === item.indexNumber,
                                  );
                                  return (
                                    <TradeDirectionRow
                                      key={item.indexNumber}
                                      station={
                                        getStationName(item.unloadingRoad, item.unloadingStation) ??
                                        "-"
                                      }
                                      tariff={item.tariffRoad ?? null}
                                      offer={offer?.wagonTariff ?? null}
                                    />
                                  );
                                })}
                                <div className="pt-0 md:pt-5">
                                  <TradeSummaryInlineRow
                                    label="Предложено"
                                    value={
                                      participant?.countWagonsOwner
                                        ? `${participant?.countWagonsOwner} в.`
                                        : "-"
                                    }
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-center md:justify-end p-5 md:p-10">
                        <Button
                          variant="outline"
                          onClick={handleExportToExcel}
                          className="text-green-500 hover:bg-green-500 border-green-500 text-sm"
                        >
                          <Download />
                          Скачать документ - Итоги .pdf
                        </Button>
                      </div>
                    </>
                  )}
                  {/* Если ни одно из условий не выполнено */}
                  {!(isWagonWinner || isWagonParticipant || isAdmin || isOrderOwner) && (
                    <div className="py-5">
                      <p className="font-normal text-sm leading-5 text-center text-Text-main">
                        Сессия прошла успешно, победители выбраны
                      </p>
                    </div>
                  )}
                </GridSection>
              </div>
            )}
          </Grid>
        </Card>

        {isWagonOwner && (
          <Card
            id="wagon-card"
            className="hide-when-empty border border-Border-gray rounded-none overflow-hidden mt-6 w-[calc(100vw-32px)] md:w-full"
          >
            {/* Своё предложение видит сам участник; отправить новое может тот, чья компания ещё
                не откликалась (без дублирования от разных сотрудников одной компании) */}
            {isCollectProposal && (isMyParticipation || !companyHasParticipation) && (
              <SobOfferForm order={details} />
            )}

            {/* Переторжку может вести только сотрудник, оформивший отклик; коллеги — только просмотр */}
            {(isRebidding || isSummingUp) && isParticipant && isMyParticipation && (
              <SobRebiddingForm order={details} />
            )}
          </Card>
        )}

        {/* Действия ГВ доступны только инициатору заявки; коллеги из компании видят заявку без кнопок */}
        {isCargoOwner && isParticipant && isСolleague && (
          <Card
            id="owner-card"
            className="hide-when-empty border border-Border-gray rounded-none overflow-hidden mt-6 w-[calc(100vw-32px)] md:w-full"
          >
            {isStartStages && <OwnerProposalForm order={details} />}

            {isEndStages && <OwnerRebiddingForm order={details} />}

            {isSummingUp && <OwnerSummingUpForm order={details} />}
          </Card>
        )}

        {isAdmin && (
          <Card
            id="admin-card"
            className="hide-when-empty border border-Border-gray rounded-none overflow-hidden mt-6 w-[calc(100vw-32px)] md:w-full"
          >
            {isStartStages && <OwnerProposalForm order={details} />}

            {isEndStages && <OwnerRebiddingForm order={details} />}
          </Card>
        )}
      </div>
    </div>
  );
}
